/**
 * stageChain — die feste 9-Slot-Kette eines Runs, geteilt von allem, was Läufe
 * und Jobs zeigt (heute: die Zeitachse `/activity` und die Run-Detailseite).
 *
 * Die Kette ist bewusst NEUN Slots lang: die acht kanonischen Stages plus
 * `jira_promote`. Die Stage existiert heute nur in `run-202610-2026-05-20-003`
 * und wurde von der alten Runs-Liste stillschweigend verschluckt; die drei
 * anderen Läufe zeigen dort jetzt die Ghost-Linie (design-hig.md ③).
 *
 * Runs, Jobs und Autoruns teilen sich dieselbe Slot-Definition — deshalb
 * springt in der Zeitachse zwischen den Bändern keine Spalte, und ein Job
 * sitzt sichtbar an seiner Stelle in der Kette *seines Runs*.
 */
import type { FlowCells, FlowSlotDef } from "@/components/flow";
import { toFlowState } from "@/components/flow";
import type { Run, StageStatus } from "@/types";

/** Die neun festen Slots. `label` steht genau einmal — in der Kopfzeile. */
export const STAGE_SLOTS: FlowSlotDef[] = [
  { id: "scrape", label: "SCR", full: "scrape" },
  { id: "mirror", label: "MIR", full: "mirror" },
  { id: "parse", label: "PAR", full: "parse" },
  { id: "generate_bpmn", label: "BPMN", full: "generate_bpmn" },
  { id: "generate_dmn", label: "DMN", full: "generate_dmn" },
  { id: "generate_ticket", label: "TICK", full: "generate_ticket" },
  { id: "jira_promote", label: "JIRA", full: "jira_promote (nur in einem Lauf vorhanden)" },
  { id: "process_repo", label: "REPO", full: "process_repo" },
  { id: "camunda_deploy", label: "DEPL", full: "camunda_deploy" },
];

/**
 * Eine Stage, wie sie in `runs.json` wirklich steht. `Run["stages"]`
 * (src/types/index.ts) kennt weder `jira_promote` noch dessen Zählfeld
 * `stories_created` — der Typ gehört einem anderen Arbeitspaket, deshalb wird
 * er hier lokal geweitet statt dort geändert.
 */
export type RunStage = StageStatus & { stories_created?: number | null };
export type RunStages = Partial<Record<string, RunStage>>;

/** Die Stages eines Runs als Slot-adressierbare Map. */
export function stagesOf(run: Run | undefined): RunStages {
  return (run?.stages ?? {}) as RunStages;
}

/**
 * Die Zahl unter dem Slot: die echte `artifacts_count` der Stage. `jira_promote`
 * zählt statt Artefakten erzeugte Stories. `undefined`/`null` heißt „keine Zahl",
 * nicht „null Artefakte" — die Ziffer 0 wäre eine andere Aussage.
 */
export function stageValue(stage: RunStage): number | null {
  return stage.artifacts_count ?? stage.stories_created ?? null;
}

/**
 * Die Kette eines Runs. Eine Stage, die der Lauf gar nicht kennt, fehlt in den
 * Zellen und wird damit zur Ghost-Linie („trifft nicht zu") — im Unterschied zu
 * `skipped` („durchgefahren, nicht gehalten"), das ein echter Zustand mit
 * eigenem Riegel ist. In `runs.json` sind das 13 von 36 Zellen `skipped`
 * gegenüber nur 3 echten Ghosts.
 */
export function runCells(run: Run): FlowCells {
  const stages = stagesOf(run);
  const cells: FlowCells = {};
  for (const slot of STAGE_SLOTS) {
    const stage = stages[slot.id];
    if (!stage) continue; // Slot bleibt leer → Ghost
    cells[slot.id] = { state: toFlowState(stage.state), value: stageValue(stage) };
  }
  return cells;
}

/**
 * Die Kette eines Jobs: der volle Run-Kontext, der Job selbst mit `here` —
 * dem Akzentstrich unter seinem Slot. Ein Job ist keine eigene Welt, sondern
 * eine Stelle in der Kette seines Runs.
 *
 * Der Job-Zustand gewinnt gegen den Stand des Runs im Journal: die Queue ist
 * live (3-s-Poll), `runs.json` ist ein Build-Artefakt.
 */
export function jobCells(
  job: { stage: string; state: string; run_id: string },
  run: Run | undefined,
): FlowCells {
  const stages = stagesOf(run);
  const cells: FlowCells = {};
  for (const slot of STAGE_SLOTS) {
    if (slot.id === job.stage) {
      cells[slot.id] = { state: toFlowState(job.state), here: true };
      continue;
    }
    const stage = stages[slot.id];
    if (!stage) continue;
    cells[slot.id] = { state: toFlowState(stage.state) };
  }
  return cells;
}
